import { PurchaseRecord } from '../../../services/purchases.service'
import { Badge } from '../../ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog'
import { DiscreteLoader } from '../../ui/discrete-loader'

interface DetailModalProps {
  showDetailModal: boolean
  setShowDetailModal: (show: boolean) => void
  selectedEntry: PurchaseRecord | null
  isLoading?: boolean
}

const money = (value: string | null | undefined, currency = 'MXN') => value == null
  ? 'Sin capturar'
  : new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value))

export function DetailModal({ showDetailModal, setShowDetailModal, selectedEntry, isLoading = false }: DetailModalProps) {
  return (
    <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Detalle de Ingreso</DialogTitle>
          <DialogDescription>Información de la nota y productos recibidos.</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="min-h-52"><DiscreteLoader text="Cargando detalle..." /></div>
        ) : selectedEntry ? (
          <div className="space-y-5">
            <dl className="grid grid-cols-1 gap-4 rounded-lg bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <div><dt className="text-xs text-slate-500">Folio</dt><dd className="font-semibold">{selectedEntry.reference_number}</dd></div>
              <div><dt className="text-xs text-slate-500">Origen</dt><dd><Badge variant="outline">{selectedEntry.source === 'excel' ? 'Excel' : 'Manual / Sin XML'}</Badge></dd></div>
              <div><dt className="text-xs text-slate-500">Proveedor</dt><dd className="font-semibold">{selectedEntry.supplier.name}</dd></div>
              <div><dt className="text-xs text-slate-500">Estado</dt><dd><Badge className="bg-green-100 text-green-700">Procesado</Badge></dd></div>
              <div><dt className="text-xs text-slate-500">Fecha de compra</dt><dd>{selectedEntry.purchase_date}</dd></div>
              <div><dt className="text-xs text-slate-500">Fecha de ingreso</dt><dd>{selectedEntry.entry_date}</dd></div>
              <div><dt className="text-xs text-slate-500">Forma de pago</dt><dd>{selectedEntry.payment_condition === 'cash' ? 'Contado' : 'Crédito'}</dd></div>
              <div><dt className="text-xs text-slate-500">Método de pago</dt><dd>{selectedEntry.payment_method.name}</dd></div>
              <div><dt className="text-xs text-slate-500">Subtotal</dt><dd>{money(selectedEntry.subtotal_amount, selectedEntry.currency)}</dd></div>
              <div><dt className="text-xs text-slate-500">Impuestos</dt><dd>{money(selectedEntry.tax_total_amount, selectedEntry.currency)}</dd></div>
              <div><dt className="text-xs text-slate-500">Total</dt><dd className="font-semibold text-green-700">{money(selectedEntry.total_amount, selectedEntry.currency)}</dd></div>
              <div><dt className="text-xs text-slate-500">Revisión de precios</dt><dd>{selectedEntry.price_review_status === 'completed' ? 'Completa' : 'Pendiente'}</dd></div>
              {selectedEntry.document_name && <div><dt className="text-xs text-slate-500">Archivo</dt><dd className="break-all">{selectedEntry.document_name}</dd></div>}
            </dl>

            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[850px]">
                <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Código</th>
                    <th className="px-4 py-3">Cantidad</th>
                    <th className="px-4 py-3">Costo</th>
                    <th className="px-4 py-3">Precio final</th>
                    <th className="px-4 py-3">Lote</th>
                    <th className="px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(selectedEntry.items ?? []).map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3"><p className="font-medium">{item.original_name}</p><p className="text-xs text-slate-500">SAT {item.sat_product_key_code || 'Sin capturar'}</p></td>
                      <td className="px-4 py-3 font-mono text-sm">{item.original_barcode}</td>
                      <td className="px-4 py-3">{item.quantity ?? 'Sin capturar'}</td>
                      <td className="px-4 py-3">{money(item.unit_cost_amount, selectedEntry.currency)}</td>
                      <td className="px-4 py-3">{money(item.proposed_sale_price_amount, selectedEntry.currency)}</td>
                      <td className="px-4 py-3">{item.lot_number ? `${item.lot_number}${item.expiration_date ? ` · ${item.expiration_date}` : ''}` : 'Sin lote'}</td>
                      <td className="px-4 py-3">
                        <Badge className={item.price_decision === 'applied' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {item.match_status === 'existing_product' ? 'Producto conciliado' : item.price_decision === 'applied' ? 'Precio aplicado' : 'Precio pendiente'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-slate-500">No fue posible cargar el detalle.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}
